import { ClickAwayListener, styled } from '@material-ui/core';

import { useEditorState } from '@tiptap/react';

import { ButtonIcon, Flex } from '@backstage/ui';

import { SemanticColor, useColorResolver } from '../../utils';
import { ColorPicker } from '../ColorPicker';
import { useCurrentEditor } from '../Context';
import { FloatingCard } from '../FloatingCard';
import { useDropdownGroup } from '../useDropdownGroup';

const ColorPickerContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: theme.spacing(1),
  zIndex: 9999,
}));

const ButtonContainer = styled('div')({
  position: 'relative',
});

const colorIconStyles = (
  textColor: string | null,
  borderColor: string | null,
) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: 'var(--bui-radius-2)',
  border: `1px solid ${borderColor ?? 'var(--bui-border)'}`,
  fontSize: 'var(--bui-font-size-2)',
  fontWeight: 500,
  color: textColor ?? 'var(--bui-fg-primary)',
});

export const TextColorButton = () => {
  const { editor } = useCurrentEditor();
  const {
    isOpen,
    toggle: handleToggle,
    close: handleClose,
  } = useDropdownGroup();
  const { resolveTextColor, resolveBackgroundColor } = useColorResolver();

  const currentTextColor = useEditorState({
    editor,
    selector: ctx =>
      ctx.editor?.getAttributes('semanticColor').color as
        | SemanticColor
        | undefined,
  });

  const currentBgColor = useEditorState({
    editor,
    selector: ctx =>
      ctx.editor?.getAttributes('semanticHighlight').color as
        | SemanticColor
        | undefined,
  });

  const isActiveCode = useEditorState({
    editor,
    selector: ctx => ctx.editor?.isActive('code') ?? false,
  });

  const handleTextColorSelect = (color: SemanticColor | null) => {
    if (color === null) {
      editor?.chain().focus().unsetSemanticColor().run();
    } else {
      editor?.chain().focus().setSemanticColor(color).run();
    }
  };

  const handleBgColorSelect = (color: SemanticColor | null) => {
    if (color === null) {
      editor?.chain().focus().unsetSemanticHighlight().run();
    } else {
      editor?.chain().focus().setSemanticHighlight(color).run();
    }
  };

  const displayColor = resolveTextColor(currentTextColor ?? null);
  const displayBgColor = resolveBackgroundColor(currentBgColor ?? null);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <ButtonContainer>
        <ButtonIcon
          size="small"
          icon={
            <div style={colorIconStyles(displayColor, displayBgColor)}>A</div>
          }
          onClick={handleToggle}
          variant="tertiary"
          isDisabled={!!isActiveCode}
          aria-label="Text color"
          data-track-id="button.text-color"
          data-track-action={isOpen ? 'collapse' : 'expand'}
        />
        {isOpen && (
          <ColorPickerContainer>
            <FloatingCard p="3" pt="2">
              <Flex direction="column" gap="4">
                <ColorPicker
                  label="Text color"
                  category="text"
                  currentValue={currentTextColor}
                  onSelect={handleTextColorSelect}
                  variant="outlined"
                  icon="A"
                />
                <ColorPicker
                  label="Background"
                  category="background"
                  currentValue={currentBgColor}
                  onSelect={handleBgColorSelect}
                  variant="filled"
                />
              </Flex>
            </FloatingCard>
          </ColorPickerContainer>
        )}
      </ButtonContainer>
    </ClickAwayListener>
  );
};
